import { Input } from '@/components/ui/input';
import { NumberInput } from '@/components/ui/number-input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { SpecTagsInput } from './SpecTagsInput';
import { getSpecField } from '@codezo/product-specs';
import { type SpecFieldRow } from '@codezo/product-specs';

/** Compact unit picker — the same affordance the dimensions fields already use. */
export function UnitToggle({
	units,
	value,
	onChange,
	disabled,
}: {
	units: readonly string[];
	value: string;
	onChange: (unit: string) => void;
	disabled?: boolean;
}) {
	return (
		<div className="flex shrink-0 gap-1">
			{units.map((unit) => (
				<button
					key={unit}
					type="button"
					disabled={disabled}
					onClick={() => onChange(unit)}
					className={cn(
						'flex h-8 cursor-pointer items-center justify-center rounded-lg border px-2 text-xs font-medium transition-colors disabled:cursor-default disabled:opacity-50',
						value === unit
							? 'border-accent bg-accent-soft text-accent'
							: 'border-line-2 bg-transparent text-text-2 hover:border-line-3'
					)}
				>
					{unit}
				</button>
			))}
		</div>
	);
}

/**
 * Renders the right-hand side of a typed specification row. Which control
 * appears is driven entirely by the field's `kind` in the registry.
 */
export function SpecValueInput({
	row,
	onChange,
	disabled,
}: {
	row: SpecFieldRow;
	onChange: (next: SpecFieldRow) => void;
	disabled?: boolean;
}) {
	const def = getSpecField(row.key);
	if (!def) return null;

	const patch = (partial: Partial<SpecFieldRow>) => onChange({ ...row, ...partial });
	const text = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
		patch({ text: e.target.value });

	switch (def.kind) {
		case 'tags':
			return (
				<SpecTagsInput
					value={row.tags}
					onChange={(tags) => patch({ tags })}
					placeholder={def.placeholder}
					disabled={disabled}
				/>
			);

		case 'longtext':
			return (
				<Textarea
					value={row.text}
					onChange={text}
					placeholder={def.placeholder}
					maxLength={def.maxLength}
					disabled={disabled}
					rows={2}
				/>
			);

		case 'number':
		case 'integer':
			return (
				<div className="flex items-center gap-2">
					<NumberInput
						value={row.text}
						onChange={(e) => patch({ text: (e.target as HTMLInputElement).value })}
						placeholder={def.placeholder ?? '0'}
						min={0}
						step={def.kind === 'integer' ? 1 : 0.01}
						disabled={disabled}
						className="max-w-[175px]"
					/>
					{def.suffix && <span className="text-xs text-text-4">{def.suffix}</span>}
				</div>
			);

		case 'measure':
			return (
				<div className="flex items-center gap-2">
					<NumberInput
						value={row.text}
						onChange={(e) => patch({ text: (e.target as HTMLInputElement).value })}
						placeholder="0"
						min={0}
						step={0.01}
						disabled={disabled}
						className="max-w-[150px]"
					/>
					<UnitToggle
						units={def.units ?? []}
						value={row.unit}
						onChange={(unit) => patch({ unit })}
						disabled={disabled}
					/>
				</div>
			);

		case 'dimensions':
			return (
				<div className="flex flex-wrap items-center gap-2">
					<div className="flex items-center gap-1">
						{(
							[
								['L', row.length, (v: string) => patch({ length: v })],
								['W', row.width, (v: string) => patch({ width: v })],
								['H', row.height, (v: string) => patch({ height: v })],
							] as const
						).map(([axis, value, setter]) => (
							<div key={axis} className="flex items-center gap-1">
								<span className="text-2xs text-text-4">{axis}</span>
								<NumberInput
									value={value}
									onChange={(e) => setter((e.target as HTMLInputElement).value)}
									placeholder="0"
									min={0}
									step={0.01}
									disabled={disabled}
									className="w-[105px]"
								/>
							</div>
						))}
					</div>
					<UnitToggle
						units={def.units ?? []}
						value={row.unit}
						onChange={(unit) => patch({ unit })}
						disabled={disabled}
					/>
				</div>
			);

		case 'text':
		default:
			return (
				<Input
					value={row.text}
					onChange={text}
					placeholder={def.placeholder}
					maxLength={def.maxLength}
					disabled={disabled}
				/>
			);
	}
}
